import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, Layers, BarChart3, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const LeanSixSigma = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Lean Six Sigma Certification Course",
    "description": "Get Lean Six Sigma certified with Green & Black Belt training. Learn DMAIC, process improvement & quality excellence from expert trainers.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isdeacademy.com"
    },
    "coursePrerequisites": "No prior experience required for Yellow Belt; Green Belt recommended for Black Belt",
    "educationalLevel": "Beginner to Advanced",
    "availableLanguage": "English",
    "inLanguage": "en",
    "audience": {
      "@type": "Audience",
      "audienceType": "Engineers, Quality Managers, Healthcare Professionals, IT Professionals, Operations Managers, Business Analysts"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P5D",
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "India"
        }
      }
    },
    "teaches": [
      "DMAIC methodology",
      "Process improvement",
      "Statistical tools",
      "Root cause analysis",
      "Waste elimination",
      "Quality management"
    ],
    "occupationalCredentialAwarded": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": "Lean Six Sigma Green Belt / Black Belt Certification"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who should enroll in a Lean Six Sigma Certification Course?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Lean Six Sigma Certification is suitable for Engineers and manufacturing professionals, Quality managers and auditors, Healthcare professionals, IT and software professionals, Operations and supply chain managers, Business analysts and consultants, Finance and service industry professionals, and Fresh graduates seeking competitive skills."
        }
      },
      {
        "@type": "Question",
        "name": "What are the levels of Lean Six Sigma Certification?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Lean Six Sigma certifications are offered at four belt levels: Yellow Belt (beginners), Green Belt (professionals leading small to medium projects), Black Belt (senior professionals and project leaders), and Master Black Belt (experts and organizational change leaders)."
        }
      },
      {
        "@type": "Question",
        "name": "What career opportunities are available after Lean Six Sigma Certification?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Certified professionals can pursue roles such as Lean Six Sigma Consultant, Quality Manager, Process Improvement Manager, Operations Manager, Business Excellence Lead, and Continuous Improvement Specialist."
        }
      }
    ]
  };

  const certificationValidation = [
    "Analyze business processes",
    "Identify root causes of problems",
    "Implement sustainable improvements",
    "Deliver measurable performance results"
  ];

  const importancePoints = [
    {
      title: "Improves Process Efficiency",
      description: "Lean Six Sigma helps organizations streamline workflows, eliminate non-value-adding activities, and optimize resources."
    },
    {
      title: "Reduces Costs and Errors",
      description: "By minimizing defects and inefficiencies, Lean Six Sigma projects often result in significant cost savings and quality improvements."
    },
    {
      title: "Enhances Customer Satisfaction",
      description: "Improved processes lead to faster delivery, better quality, and consistent performance—key drivers of customer satisfaction."
    },
    {
      title: "Builds Data-Driven Decision Makers",
      description: "Professionals learn to rely on data, metrics, and statistical tools rather than assumptions."
    },
    {
      title: "Boosts Career Growth and Recognition",
      description: "Lean Six Sigma certification is globally recognized and highly valued by employers across industries."
    }
  ];

  const leanFocus = [
    "Eliminating waste (non-value-added activities)",
    "Improving flow and efficiency",
    "Enhancing customer value"
  ];

  const sixSigmaFocus = [
    "Reducing process variation",
    "Improving quality",
    "Achieving near-perfect performance"
  ];

  const dmaicFramework = [
    { phase: "Define", description: "Identify the problem and objectives" },
    { phase: "Measure", description: "Collect and analyze data" },
    { phase: "Analyze", description: "Identify root causes" },
    { phase: "Improve", description: "Implement solutions" },
    { phase: "Control", description: "Sustain improvements" }
  ];

  const targetAudience = [
    "Engineers and manufacturing professionals",
    "Quality managers and auditors",
    "Healthcare professionals",
    "IT and software professionals",
    "Operations and supply chain managers",
    "Business analysts and consultants",
    "Finance and service industry professionals",
    "Fresh graduates seeking competitive skills"
  ];

  const beltLevels = [
    {
      title: "Lean Six Sigma Yellow Belt Certification",
      ideal: "Beginners and team members",
      focus: "Basic concepts, tools, and terminology",
      topics: ["Introduction to Lean and Six Sigma", "Process mapping", "Basic problem-solving tools", "Waste identification"]
    },
    {
      title: "Lean Six Sigma Green Belt Certification",
      ideal: "Professionals leading small to medium projects",
      focus: "Practical application of DMAIC",
      topics: ["Statistical tools", "Root cause analysis", "Data analysis", "Process improvement projects"],
      note: "Green Belt professionals actively contribute to organizational improvement initiatives."
    },
    {
      title: "Lean Six Sigma Black Belt Certification",
      ideal: "Senior professionals and project leaders",
      focus: "Advanced analytics and leadership",
      topics: ["Advanced statistical methods", "Change management", "Project leadership", "Strategic problem solving"],
      note: "Black Belts lead high-impact improvement projects across departments."
    },
    {
      title: "Lean Six Sigma Master Black Belt Certification",
      ideal: "Experts and organizational change leaders",
      focus: "Coaching, mentoring, and strategy",
      topics: ["Train and mentor belts", "Guide organizational transformation", "Align improvement initiatives with business strategy"]
    }
  ];

  const benefits = [
    {
      title: "High Return on Investment",
      description: "Organizations often recover training costs through project savings delivered by certified professionals."
    },
    {
      title: "Global Recognition",
      description: "Lean Six Sigma certification is recognized worldwide across industries."
    },
    {
      title: "Career Advancement",
      description: "Certified professionals often qualify for leadership roles, promotions, and higher salaries."
    },
    {
      title: "Versatility Across Industries",
      description: "Lean Six Sigma principles apply to manufacturing, healthcare, IT, finance, logistics, and services."
    },
    {
      title: "Continuous Improvement Mindset",
      description: "Certification fosters a culture of innovation, accountability, and continuous improvement."
    }
  ];

  const providerCriteria = [
    "Globally recognized certification",
    "Experienced and certified trainers",
    "Practical, project-based approach",
    "Industry-relevant case studies",
    "Post-training support and mentoring"
  ];

  const careerOpportunities = [
    "Lean Six Sigma Consultant",
    "Quality Manager",
    "Process Improvement Manager",
    "Operations Manager",
    "Business Excellence Lead",
    "Continuous Improvement Specialist"
  ];

  const futureScope = [
    "Healthcare process optimization",
    "Digital and IT process improvement",
    "Sustainability and green operations",
    "Data-driven business transformation"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Lean Six Sigma Certification Course | Green & Black Belt"
        description="Get Lean Six Sigma certified with Green & Black Belt training. Learn DMAIC, process improvement & quality excellence from expert trainers."
        keywords="Lean Six Sigma Certification Course, Lean Six Sigma training, Green Belt certification, Black Belt certification, DMAIC, process improvement, quality management, Six Sigma training, Lean manufacturing, continuous improvement, waste reduction, Lean Six Sigma India, Lean Six Sigma Chennai, Lean Six Sigma Bangalore, Six Sigma certification India"
        canonical="/Lean-Six-Sigma-Certification-Course"
        structuredData={[structuredData, faqStructuredData]}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'Lean Six Sigma', path: '/Lean-Six-Sigma-Certification-Course' }
        ]}
      />

      {/* Hero Section with H1 */}
      <header className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Lean Six Sigma Certification Course
            </h1>
          </motion.div>
        </div>
      </header>

      {/* Back Button */}
      <nav className="bg-gray-50 py-6" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-12">
        <article>
          {/* Introduction Section */}
          <section className="mb-16">
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  In today's competitive and fast-evolving business environment, organizations are under constant pressure to improve efficiency, reduce costs, and deliver higher quality products and services. This is where the <strong>Lean Six Sigma Certification Course</strong> plays a vital role. Lean Six Sigma is a globally recognized methodology that combines Lean principles for waste reduction with Six Sigma tools for process improvement and defect reduction.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A <strong>Lean Six Sigma Certification Course</strong> equips professionals with the skills, tools, and mindset needed to identify inefficiencies, improve processes, and drive measurable business results. From manufacturing and healthcare to IT, finance, and service industries, Lean Six Sigma has become a core competency for professionals seeking career advancement and organizational impact.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A Lean Six Sigma Certification Course is a structured training program designed to teach individuals how to apply Lean and Six Sigma methodologies to improve processes, eliminate waste, and reduce variation. The course focuses on data-driven decision-making, continuous improvement, and customer satisfaction.
                </p>
                <p className="text-lg text-gray-700 mb-4">Lean Six Sigma certification validates a professional's ability to:</p>
                <ul className="grid md:grid-cols-2 gap-3 list-none">
                  {certificationValidation.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Importance Section - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-green-600" aria-hidden="true" />
                Importance of Lean Six Sigma Certification Course
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {importancePoints.map((point, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-green-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-green-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold mb-2 text-gray-900">{point.title}</h4>
                          <p className="text-gray-700 text-sm">{point.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Methodology Section - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Layers className="h-8 w-8 text-green-600" aria-hidden="true" />
                Lean Six Sigma Methodology Explained
              </h2>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">Lean Six Sigma integrates two powerful approaches:</p>

                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                      <h4 className="text-xl font-bold mb-4 text-gray-900">Lean</h4>
                      <p className="text-gray-700 mb-4">Lean focuses on:</p>
                      <ul className="space-y-2 list-none">
                        {leanFocus.map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-teal-50 p-6 rounded-xl border border-teal-200">
                      <h4 className="text-xl font-bold mb-4 text-gray-900">Six Sigma</h4>
                      <p className="text-gray-700 mb-4">Six Sigma focuses on:</p>
                      <ul className="space-y-2 list-none">
                        {sixSigmaFocus.map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-1" aria-hidden="true" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl">
                    <p className="text-lg text-gray-700 mb-4 font-semibold">Together, they use the DMAIC framework:</p>
                    <div className="grid md:grid-cols-5 gap-4">
                      {dmaicFramework.map((item, index) => (
                        <div key={index} className="text-center bg-white p-4 rounded-lg shadow-sm">
                          <div className="bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">
                            {item.phase[0]}
                          </div>
                          <h5 className="font-bold text-gray-900">{item.phase}</h5>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Who Should Enroll - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Users className="h-7 w-7 text-green-600" aria-hidden="true" />
                Who Should Enroll in a Lean Six Sigma Certification Course?
              </h3>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">Lean Six Sigma Certification is suitable for:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {targetAudience.map((audience, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{audience}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Levels of Certification - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Award className="h-7 w-7 text-green-600" aria-hidden="true" />
                Levels of Lean Six Sigma Certification
              </h4>
              <p className="text-lg text-gray-700 mb-6">Lean Six Sigma Certification Courses are offered at different belt levels, each representing a specific level of expertise.</p>
              <div className="space-y-6">
                {beltLevels.map((belt, index) => (
                  <Card key={index} className={`border-l-4 ${index === 0 ? 'border-l-yellow-500' : index === 1 ? 'border-l-green-500' : index === 2 ? 'border-l-gray-900' : 'border-l-purple-600'}`}>
                    <CardContent className="p-6">
                      <h5 className="text-xl font-bold mb-3 text-gray-900">{index + 1}. {belt.title}</h5>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <p className="text-gray-700"><strong>Ideal for:</strong> {belt.ideal}</p>
                        <p className="text-gray-700"><strong>Focus:</strong> {belt.focus}</p>
                      </div>
                      <p className="text-gray-700 mb-2">Key topics include:</p>
                      <ul className="grid md:grid-cols-2 gap-2 list-none mb-2">
                        {belt.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <span className="text-gray-700 text-sm">{topic}</span>
                          </li>
                        ))}
                      </ul>
                      {belt.note && <p className="text-gray-600 italic text-sm mt-2">{belt.note}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Benefits - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <BarChart3 className="h-7 w-7 text-green-600" aria-hidden="true" />
                Benefits of Lean Six Sigma Certification Course
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow bg-gradient-to-br from-green-50 to-teal-50">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-green-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-green-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-lg font-bold mb-2 text-gray-900">{benefit.title}</h5>
                          <p className="text-gray-700 text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Choosing Provider - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-green-600" aria-hidden="true" />
                Choosing the Right Lean Six Sigma Certification Course Provider
              </h4>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">When selecting a training provider, look for:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {providerCriteria.map((criteria, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{criteria}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    Accreditation and industry credibility add significant value to the certification.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Career Opportunities - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Briefcase className="h-7 w-7 text-green-600" aria-hidden="true" />
                Career Opportunities After Lean Six Sigma Certification
              </h4>
              <Card className="bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">Certified professionals can pursue roles such as:</p>
                  <ul className="grid md:grid-cols-3 gap-4 mb-6 list-none">
                    {careerOpportunities.map((opportunity, index) => (
                      <li key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700 font-semibold">{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 font-semibold">
                    Lean Six Sigma skills are highly transferable and future-proof.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Future Scope - H5 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h5 className="text-xl md:text-2xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Target className="h-6 w-6 text-green-600" aria-hidden="true" />
                Future Scope of Lean Six Sigma Certification
              </h5>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">
                    With increasing focus on operational excellence, digital transformation, and cost optimization, Lean Six Sigma remains highly relevant. Emerging areas include:
                  </p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {futureScope.map((scope, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Shield className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{scope}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    Lean Six Sigma continues to evolve alongside modern business needs.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <Briefcase className="h-8 w-8 text-green-600" aria-hidden="true" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqStructuredData.mainEntity.map((faq, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.name}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.acceptedAnswer.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>
        </article>

        {/* Related Courses */}
        <RelatedCoursesSection currentSlug="iso-9001" slugs={['iso-9001', 'iso-14001', 'iatf-16949']} heading="Related ISO Training Courses" />

        {/* CTA Section */}
        <aside>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-green-600 to-teal-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Get Lean Six Sigma Certified?
                </h2>
                <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
                  Join thousands of professionals who have advanced their careers with our comprehensive Lean Six Sigma Green Belt and Black Belt certification programs.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" aria-hidden="true" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </aside>
      </main>
    </div>
  );
};
